import { type CrowdexUIMessagePart, toolImageCallId } from '@crowdex/agent-contracts';
import { isBackgroundTerminal } from './terminal';
import { isQuestionToolPart } from './tool-identity';
import type {
  DynamicToolPart,
  FilePart,
  SourcePart,
  TerminalPart,
  ToolActivityPart,
} from './types';

export function partToolCallId(part: CrowdexUIMessagePart): string | null {
  if (part.type === 'dynamic-tool') return part.toolCallId;
  if (part.type === 'data-diff' || part.type === 'data-terminal') return part.data.toolCallId;
  return null;
}

export function isToolActivityPart(part: CrowdexUIMessagePart): part is ToolActivityPart {
  return part.type === 'dynamic-tool' || part.type === 'data-diff' || part.type === 'data-terminal';
}

/** An image a tool returned (a screenshot, a generated photo): shown under its step. */
function isToolImagePart(part: CrowdexUIMessagePart | undefined): part is FilePart {
  return part?.type === 'file' && toolImageCallId(part) !== null;
}

/**
 * A single unit the message view renders. Tool activity is either shown on its
 * own (`single-activity`, which renders its own header) or collapsed into a
 * `group` card; every other part is a `standalone` render, with questions and
 * questions carved out because they use bespoke cards.
 */
export type MessagePartRenderItem =
  | { kind: 'question'; key: string; part: DynamicToolPart }
  | { kind: 'sources'; key: string; parts: SourcePart[] }
  | { kind: 'standalone'; key: string; part: CrowdexUIMessagePart; isLast: boolean }
  | { kind: 'single-activity'; key: string; part: ToolActivityPart }
  | { kind: 'tool-terminal'; key: string; tool: DynamicToolPart; terminal: TerminalPart }
  | {
      kind: 'background-terminal';
      key: string;
      tool: DynamicToolPart;
      terminal: TerminalPart;
    }
  | { kind: 'group'; key: string; parts: ToolActivityPart[] }
  /** The images tools returned, as one strip of thumbnails where those tools ran. */
  | { kind: 'tool-images'; key: string; parts: FilePart[] };

function backgroundTerminalPair(
  tool: CrowdexUIMessagePart | undefined,
  terminal: CrowdexUIMessagePart | undefined,
): { tool: DynamicToolPart; terminal: TerminalPart } | null {
  if (
    tool?.type !== 'dynamic-tool' ||
    terminal?.type !== 'data-terminal' ||
    terminal.data.toolCallId !== tool.toolCallId ||
    !isBackgroundTerminal(terminal.data.output)
  )
    return null;
  return { tool, terminal };
}

/**
 * Turn a message's parts into the ordered render plan for the desktop view.
 *
 * Subagent-owned tool calls are dropped (they render inside their card),
 * consecutive tool activity is coalesced into groups, plan submissions are
 * hidden once a final plan exists, and every item carries the exact React key
 * the previous inline implementation produced so remounts stay identical.
 */
export function groupMessageParts(
  messageId: string,
  parts: readonly CrowdexUIMessagePart[],
  subagentToolCallIds: ReadonlySet<string>,
): { items: MessagePartRenderItem[]; visibleParts: CrowdexUIMessagePart[] } {
  const filteredParts = parts.filter((part) => {
    const id = partToolCallId(part);
    return id === null || !subagentToolCallIds.has(id);
  });
  // Terminal events can arrive before their tool, or after intervening text.
  // Associate by call ID before grouping instead of relying on event adjacency.
  const tools = new Set(
    filteredParts.filter((part) => part.type === 'dynamic-tool').map((part) => part.toolCallId),
  );
  const terminals = new Map(
    filteredParts
      .filter((part): part is TerminalPart => part.type === 'data-terminal')
      .map((part) => [part.data.toolCallId, part] as const),
  );
  const visibleParts = filteredParts.flatMap((part): CrowdexUIMessagePart[] => {
    if (part.type === 'data-terminal' && tools.has(part.data.toolCallId)) return [];
    const terminal = part.type === 'dynamic-tool' ? terminals.get(part.toolCallId) : undefined;
    return terminal === undefined ? [part] : [part, terminal];
  });
  const items: MessagePartRenderItem[] = [];

  for (let index = 0; index < visibleParts.length; index += 1) {
    const part = visibleParts[index] as CrowdexUIMessagePart;
    if (part.type === 'source-url' || part.type === 'source-document') {
      const sources: SourcePart[] = [part];
      while (
        visibleParts[index + 1]?.type === 'source-url' ||
        visibleParts[index + 1]?.type === 'source-document'
      ) {
        index += 1;
        sources.push(visibleParts[index] as SourcePart);
      }
      items.push({ kind: 'sources', key: `${messageId}-sources-${index}`, parts: sources });
      continue;
    }
    if (isToolImagePart(part)) {
      const images: FilePart[] = [part];
      while (isToolImagePart(visibleParts[index + 1])) {
        index += 1;
        images.push(visibleParts[index] as FilePart);
      }
      items.push({ kind: 'tool-images', key: `${messageId}-images-${index}`, parts: images });
      continue;
    }
    if (part.type === 'dynamic-tool' && isQuestionToolPart(part)) {
      items.push({ kind: 'question', key: `${messageId}-${part.toolCallId}`, part });
      continue;
    }
    if (!isToolActivityPart(part)) {
      items.push({
        kind: 'standalone',
        key: `${messageId}-${index}`,
        part,
        isLast: index === visibleParts.length - 1,
      });
      continue;
    }

    const backgroundTerminal = backgroundTerminalPair(part, visibleParts[index + 1]);
    if (backgroundTerminal !== null) {
      items.push({
        kind: 'background-terminal',
        key: `${messageId}-${backgroundTerminal.tool.toolCallId}`,
        ...backgroundTerminal,
      });
      index += 1;
      continue;
    }

    const activity: ToolActivityPart[] = [part];
    // A tool's images do not split its run of steps: they show after it, as one strip.
    const images: FilePart[] = [];
    while (true) {
      let next = index + 1;
      while (isToolImagePart(visibleParts[next])) next += 1;
      const candidate = visibleParts[next];
      if (
        candidate === undefined ||
        !isToolActivityPart(candidate) ||
        backgroundTerminalPair(candidate, visibleParts[next + 1]) !== null ||
        isQuestionToolPart(candidate)
      )
        break;
      for (let image = index + 1; image < next; image += 1)
        images.push(visibleParts[image] as FilePart);
      index = next;
      activity.push(candidate);
    }
    // A single activity already renders its own header/content, so wrapping it
    // in a group card would duplicate the header. Only group when there are 2+.
    if (
      activity.length === 2 &&
      activity[0]?.type === 'dynamic-tool' &&
      activity[1]?.type === 'data-terminal' &&
      activity[0].toolCallId === activity[1].data.toolCallId
    ) {
      items.push({
        kind: 'tool-terminal',
        key: `${messageId}-${activity[0].toolCallId}`,
        tool: activity[0],
        terminal: activity[1],
      });
    } else if (activity.length === 1) {
      const single = activity[0] as ToolActivityPart;
      items.push({
        kind: 'single-activity',
        key: `${messageId}-${partToolCallId(single) ?? index}`,
        part: single,
      });
    } else {
      items.push({
        kind: 'group',
        key: `${messageId}-${partToolCallId(part) ?? index}`,
        parts: activity,
      });
    }
    while (isToolImagePart(visibleParts[index + 1])) {
      index += 1;
      images.push(visibleParts[index] as FilePart);
    }
    if (images.length > 0)
      items.push({ kind: 'tool-images', key: `${messageId}-images-${index}`, parts: images });
  }

  return { items, visibleParts };
}
